/**
 * Identity-stable list replacement for poll-refreshed state.
 *
 * The messaging surfaces poll (thread list every 8s, open thread every 5s).
 * Each poll used to call `setState(freshArray)` with brand-new item objects,
 * so React re-rendered every row of every list even when nothing had changed —
 * the main source of scroll/typing jank on the native WebViews.
 *
 * `stabilizeList` keeps the previous object identity for every item that is
 * deep-equal to its replacement, and returns the previous ARRAY identity when
 * the whole list is unchanged, so `setState` becomes a no-op re-render-wise.
 */

/** Deep equality over JSON-shaped values (plain objects, arrays, primitives). */
export function deepEqualJson(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (typeof a !== typeof b) return false
  if (a === null || b === null) return false
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if (!deepEqualJson(a[i], b[i])) return false
    }
    return true
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>
    const aKeys = Object.keys(aObj)
    const bKeys = Object.keys(bObj)
    if (aKeys.length !== bKeys.length) return false
    for (const key of aKeys) {
      if (!Object.prototype.hasOwnProperty.call(bObj, key)) return false
      if (!deepEqualJson(aObj[key], bObj[key])) return false
    }
    return true
  }
  return false
}

/**
 * Return `next` with item/array identities reused from `prev` wherever the
 * content is deep-equal. If every item is unchanged and the order/length
 * match, the exact `prev` array is returned (so `setState(stabilizeList(...))`
 * skips the re-render entirely).
 */
export function stabilizeList<T>(
  prev: readonly T[],
  next: readonly T[],
  keyOf: (item: T) => string,
): T[] {
  if (prev === next) return next as T[]
  const prevByKey = new Map<string, T>()
  for (const item of prev) prevByKey.set(keyOf(item), item)

  let allSame = prev.length === next.length
  const out: T[] = new Array(next.length)
  for (let i = 0; i < next.length; i++) {
    const incoming = next[i]
    const old = prevByKey.get(keyOf(incoming))
    if (old !== undefined && (old === incoming || deepEqualJson(old, incoming))) {
      out[i] = old
      if (allSame && prev[i] !== old) allSame = false
    } else {
      out[i] = incoming
      allSame = false
    }
  }
  return allSame ? (prev as T[]) : out
}
